import { ValidatorInterface } from './ValidatorInterface';
import { AbstractValidator, ValidatorObject } from './AbstractValidator';
import { sprintf } from 'sprintf-js';
import Length from '../../../../source/js/shared/models/Geometry/Length';
import { Trapezoid } from '../Shapes/Trapezoid';

interface TrapezoidConstraintValidatorOptions extends ValidatorObject {
	maximums: Record<string, Length>;
}

/**
 * Validator for trapezoid-specific dimensional constraints:
 * - If height = maximum then width_top and width_bottom ≤ half of maximum
 * - If width_top = maximum then width_bottom can be maximum but height ≤ half of maximum
 * - If width_bottom = maximum, width_top can be maximum but height ≤ half of maximum
 */
export class TrapezoidConstraintValidator extends AbstractValidator implements ValidatorInterface {
	private readonly trapezoidMaximums: Record<string, Length>;

	constructor(options: TrapezoidConstraintValidatorOptions) {
		super(options);
		this.trapezoidMaximums = options.maximums;
	}

	getMessage(): string {
		if (super.getCustomMessage()) {
			return super.getCustomMessage();
		}

		const labels = this.getValidatorTranslationStrings().dimensions.labels;
		const maxDimension = this.getMaximumDimension();
		const halfMaximum = Math.floor(maxDimension / 2);
		const trapezoid = this.instance as Trapezoid;

		let maxDimensionLabel: string;
		let constraintDimensionsLabel: string;

		if (Math.abs(trapezoid.getHeight().in_cm() - maxDimension) < 0.1) {
			maxDimensionLabel = labels.height;
			constraintDimensionsLabel = sprintf(window.measurementSelectorVariables.i18n.validators.dimensions.constraintMaxBoth, labels.width_top, labels.width_bottom);
		} else if (Math.abs(trapezoid.getWidthTop().in_cm() - maxDimension) < 0.1) {
			maxDimensionLabel = labels.width_top;
			constraintDimensionsLabel = labels.height;
		} else if (Math.abs(trapezoid.getWidthBottom().in_cm() - maxDimension) < 0.1) {
			maxDimensionLabel = labels.width_bottom;
			constraintDimensionsLabel = labels.height;
		}

		return sprintf(
			window.measurementSelectorVariables.i18n.validators.dimensions.constraintMax,
			maxDimensionLabel.toLowerCase(),
			maxDimension,
			constraintDimensionsLabel.toLowerCase(),
			halfMaximum,
			'cm'
		);
	}

	isValid(): boolean {

		// Constraints don't apply to cutouts
		if (this.getIsCutout()) {
			return true;
		}

		const maxDimension = this.getMaximumDimension();
		const halfMaximum = maxDimension / 2;
		const trapezoid = this.instance as Trapezoid;

		const heightValue = trapezoid.getHeight().in_cm();
		const widthTopValue = trapezoid.getWidthTop().in_cm();
		const widthBottomValue = trapezoid.getWidthBottom().in_cm();

		if (Math.abs(heightValue - maxDimension) < 0.1) {
			return widthTopValue <= halfMaximum && widthBottomValue <= halfMaximum;
		}

		if (Math.abs(widthTopValue - maxDimension) < 0.1) {
			return heightValue <= halfMaximum;
		}

		if (Math.abs(widthBottomValue - maxDimension) < 0.1) {
			return heightValue <= halfMaximum;
		}

		return true;
	}

	private getMaximumDimension(): number {
		const heightMax = this.trapezoidMaximums.height.in_cm();
		const widthTopMax = this.trapezoidMaximums.width_top.in_cm();
		const widthBottomMax = this.trapezoidMaximums.width_bottom.in_cm();

		// They should all be the same, but take the maximum to be safe
		return Math.max(heightMax, widthTopMax, widthBottomMax);
	}
}
